import { useState, useEffect, useCallback } from 'react';
import { reservationAdminService } from '@services/reservationAdminService';
import { ReservationResponseDto, PagedReservationResponseDto } from '../types/reservation';
import { ReservationStatus } from '@/types';
import { useErrorHandler } from './useErrorHandler';

interface UseAdminReservationsOptions {
  initialPage?: number;
  initialLimit?: number;
}

export const useAdminReservations = (opts: UseAdminReservationsOptions = {}) => {
  const [data, setData] = useState<ReservationResponseDto[]>([]);
  const [page, setPage] = useState<number>(opts.initialPage || 1);
  const [limit, setLimit] = useState<number>(opts.initialLimit || 10);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchPage = useCallback(async (p = page, l = limit, status?: string) => {
    setLoading(true);
    setError(null);
    try {
  const res: PagedReservationResponseDto = await reservationAdminService.getReservations(p, l, status);
      setData(res.data);
      setTotal(res.total);
      setPage(res.page);
      setLimit(res.limit);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchPage(page, limit, statusFilter);
  }, [fetchPage, page, limit, statusFilter]);

  const refresh = useCallback(() => fetchPage(page, limit, statusFilter), [fetchPage, page, limit, statusFilter]);
  const { showSuccess } = useErrorHandler();

  // simple client-side search over loaded page
  const filtered = data.filter(r => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.reservationCode.toLowerCase().includes(q) ||
      r.userId.toLowerCase().includes(q) ||
      r.equipmentId.toLowerCase().includes(q)
    );
  });

  return {
    data: filtered,
    rawData: data,
    page,
    limit,
    total,
    loading,
    error,
    setPage,
    setLimit,
    setStatusFilter,
    statusFilter,
    setSearchTerm,
    searchTerm,
    refresh,
    // Admin actions
    confirmReservation: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.confirmReservation(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    cancelReservation: async (id: string, reason?: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.cancelReservation(id, reason);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    startPreparation: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.startPreparation(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    markDelivered: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.markDelivered(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    markActive: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.markActive(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    markPendingReturn: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.markPendingReturn(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    markReturned: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.markReturned(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    completeReservation: async (id: string) => {
      setLoading(true);
      try {
        const res = await reservationAdminService.completeReservation(id);
        await fetchPage(page, limit, statusFilter);
        return res;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    /**
     * Performs the appropriate admin action based on the target status.
     * Accepts the current reservation object (so we can inspect current status)
     * and the desired target status string. Returns the updated reservation.
     */
    performStatusTransition: async (reservation: ReservationResponseDto, targetStatus: string) => {
      const from = String(reservation.status ?? '');
      const to = String(targetStatus);
      if (from === to) return reservation;

      setLoading(true);
      try {
        // Map target status to admin service calls
          let result = null;
          switch (to) {
            case ReservationStatus.CONFIRMED: {
              result = await reservationAdminService.confirmReservation(reservation.id);
              showSuccess('Reserva confirmada');
              break;
            }
            case ReservationStatus.CANCELED: {
              result = await reservationAdminService.cancelReservation(reservation.id);
              showSuccess('Reserva cancelada');
              break;
            }
            case ReservationStatus.PREPARING_SHIPMENT: {
              result = await reservationAdminService.startPreparation(reservation.id);
              showSuccess('Reserva en preparación');
              break;
            }
            case ReservationStatus.IN_DELIVERY: {
              result = await reservationAdminService.markDelivered(reservation.id);
              showSuccess('Reserva entregada');
              break;
            }
            case ReservationStatus.ACTIVE: {
              result = await reservationAdminService.markActive(reservation.id);
              showSuccess('Reserva activada');
              break;
            }
            case ReservationStatus.PENDING_RETURN: {
              result = await reservationAdminService.markPendingReturn(reservation.id);
              showSuccess('Marca como pendiente de devolución');
              break;
            }
            case ReservationStatus.IN_RETURN: {
              result = await reservationAdminService.markInReturn(reservation.id);
              showSuccess('Reserva en devolución');
              break;
            }
            case ReservationStatus.RETURNED: {
              result = await reservationAdminService.markReturned(reservation.id);
              showSuccess('Reserva devuelta');
              break;
            }
            case ReservationStatus.COMPLETED: {
              result = await reservationAdminService.completeReservation(reservation.id);
              showSuccess('Reserva completada');
              break;
            }
            default:
              return null;
          }
          return result;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        // refresh current page after transition
        try {
          await fetchPage(page, limit, statusFilter);
        } catch (refreshErr) {
          console.warn('Failed to refresh after status transition', refreshErr);
        }
        setLoading(false);
      }
    },
  } as const;
};
